import ExcelJS from 'exceljs';
import { downloadBlob } from './download';
import { eachDayOfInterval, parseISO, format, isWeekend } from 'date-fns';
import type { ScheduleItem } from '../stores/scheduleStore';
import type { LeaveEntry } from '../stores/leaveStore';
import type { UserHoliday } from '../hooks/useHolidays';
import type { WorkloadEntry, WorkloadIndicatorType } from '../stores/workloadStore';
import { getWorkloadColor, WORKLOAD_COLORS } from './colors';
import { computeRemainingPerPerson } from '../services/workload-data.service';
import { formatHoursMinutes } from './timesheet-capacity';

interface ExportUser {
  id: number;
  name: string;
}

export interface ExportOptions {
  dateRange: { start: string; end: string };
  items: ScheduleItem[];
  leaves: LeaveEntry[];
  userHolidays: UserHoliday[];
  users: ExportUser[];
  hideWeekends: boolean;
  /** Workload entries indexed by userId */
  workloadByUser: Map<number, WorkloadEntry[]>;
  /** Active workload indicator type */
  indicatorType: WorkloadIndicatorType | null;
  /** Per-item total worklog hours (mondayItemId → totalHours) for remaining estimate calc */
  worklogsByItem: Map<string, number>;
}

/** Convert a hex color like "#579bfc" to an ExcelJS ARGB string like "FF579BFC" */
function hexToArgb(hex: string): string {
  const clean = hex.replace('#', '').toUpperCase();
  return `FF${clean.padEnd(6, '0')}`;
}

/** Lighten a hex color for use as a cell background */
function lightenHex(hex: string, factor = 0.35): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * factor);
  const lg = Math.round(g + (255 - g) * factor);
  const lb = Math.round(b + (255 - b) * factor);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

const thinBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FFE0E0E0' } };

export async function exportSchedulerExcel(options: ExportOptions): Promise<void> {
  const { dateRange, items, leaves, userHolidays, users, hideWeekends, workloadByUser, indicatorType, worklogsByItem } = options;

  const isRemainingMode = indicatorType === 'remaining_estimate' || indicatorType === 'worklog_workload' || indicatorType === 'availability';

  const hoursLabel: Record<string, string> = {
    workload: 'Original Estimate',
    remaining_estimate: 'Remaining Estimate',
    worklog_workload: 'Worklog + Workload',
    availability: 'Availability',
    issues: 'Number of Items',
  };
  const activeMode = indicatorType ? (hoursLabel[indicatorType] ?? 'Workload') : 'Original Estimate';

  const startDate = parseISO(dateRange.start);
  const endDate = parseISO(dateRange.end);
  let allDays = eachDayOfInterval({ start: startDate, end: endDate });
  if (hideWeekends) {
    allDays = allDays.filter((d) => !isWeekend(d));
  }

  // Build a map: userId → items sorted by start date
  const itemsByUser = new Map<number, ScheduleItem[]>();
  for (const item of items) {
    const list = itemsByUser.get(item.assigneeId) ?? [];
    list.push(item);
    itemsByUser.set(item.assigneeId, list);
  }

  // Build a map: `${userId}_${dateStr}` → LeaveEntry
  const leaveMap = new Map<string, LeaveEntry>();
  for (const leave of leaves) {
    if (leave.status === 'Rejected') continue;
    const ls = parseISO(leave.startDate);
    const le = parseISO(leave.endDate);
    const leaveDays = eachDayOfInterval({ start: ls, end: le });
    for (const d of leaveDays) {
      const key = `${leave.assigneeId}_${format(d, 'yyyy-MM-dd')}`;
      leaveMap.set(key, leave);
    }
  }

  // Build a map: `${userId}_${dateStr}` → holiday name
  const holidayMap = new Map<string, string>();
  for (const h of userHolidays) {
    for (const uid of h.assignedUserIds) {
      holidayMap.set(`${uid}_${h.date}`, h.name);
    }
  }

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Schedule');

  // ── Title ──
  const dateLabel = `${format(startDate, 'MMM d, yyyy')} — ${format(endDate, 'MMM d, yyyy')}`;
  const titleRow = ws.addRow(['Schedule Export: ' + dateLabel]);
  titleRow.font = { bold: true, size: 14 };
  titleRow.height = 28;
  ws.addRow([]); // spacer

  // ── Header row: "Resource" + date columns ──
  const headerRow = ws.addRow([
    'Resource',
    ...allDays.map((d) => format(d, 'EEE, MMM d')),
  ]);
  headerRow.font = { bold: true, size: 10 };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  headerRow.height = 24;

  // Freeze pane below the header row
  ws.views = [{ state: 'frozen' as const, xSplit: 0, ySplit: headerRow.number }];

  // Style header cells
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF323338' } };
    cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FF999999' } },
      right: { style: 'thin', color: { argb: 'FF555555' } },
    };

    // Highlight weekend columns in header
    if (colNumber > 1) {
      const dayIndex = colNumber - 2;
      if (dayIndex < allDays.length && isWeekend(allDays[dayIndex])) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A4A4F' } };
      }
    }
  });

  // Set column widths
  ws.getColumn(1).width = 22;
  for (let i = 2; i <= allDays.length + 1; i++) {
    ws.getColumn(i).width = 16;
  }

  // ── One section per user ──
  for (const user of users) {
    const userItems = itemsByUser.get(user.id) ?? [];

    // Build day → items map for this user
    const dayItemsMap = new Map<string, ScheduleItem[]>();
    for (const item of userItems) {
      const is = parseISO(item.startDate);
      const ie = parseISO(item.endDate);
      const itemDays = eachDayOfInterval({ start: is, end: ie });
      for (const d of itemDays) {
        const ds = format(d, 'yyyy-MM-dd');
        const list = dayItemsMap.get(ds) ?? [];
        list.push(item);
        dayItemsMap.set(ds, list);
      }
    }

    // Track days that have a leave or holiday so we reserve a slot for them
    const daysWithLeave = new Set<string>();
    for (const day of allDays) {
      const ds = format(day, 'yyyy-MM-dd');
      const key = `${user.id}_${ds}`;
      if (leaveMap.has(key) || holidayMap.has(key)) {
        daysWithLeave.add(ds);
      }
    }

    // maxOverlap must account for leave needing its own slot when items fill all slots
    let maxOverlap = 1;
    for (const day of allDays) {
      const ds = format(day, 'yyyy-MM-dd');
      const itemCount = dayItemsMap.get(ds)?.length ?? 0;
      const needsLeaveSlot = daysWithLeave.has(ds) ? 1 : 0;
      maxOverlap = Math.max(maxOverlap, itemCount + needsLeaveSlot);
    }

    // Build workload lookup for this user: dateStr → WorkloadEntry
    const userWorkload = new Map<string, WorkloadEntry>();
    const entries = workloadByUser.get(user.id) ?? [];
    for (const e of entries) {
      userWorkload.set(e.date, e);
    }

    const sectionStartRow = ws.rowCount + 1;

    // ── Item rows ──
    for (let slot = 0; slot < maxOverlap; slot++) {
      const rowData: string[] = [slot === 0 ? user.name : ''];
      const cellColors: (string | null)[] = [null];
      const cellFontColors: (string | null)[] = [null];

      for (const day of allDays) {
        const ds = format(day, 'yyyy-MM-dd');
        const dayItems = dayItemsMap.get(ds) ?? [];
        const item = dayItems[slot];

        if (item) {
          let displayHours = item.workHourFormat === 'per_day' ? item.hoursPerDay : item.hoursTotal;
          let hoursSuffix = item.workHourFormat === 'per_day' ? '/d' : '';

          if (isRemainingMode && item.workHourFormat === 'total' && displayHours > 0) {
            const itemWorklogs = worklogsByItem.get(String(item.mondayItemId)) ?? 0;
            if (itemWorklogs > 0) {
              const originalTotal = item.originalTotalHours ?? item.hoursTotal;
              displayHours = computeRemainingPerPerson(originalTotal, itemWorklogs, 1);
            }
            hoursSuffix = ' rem';
          }

          const hoursLabel = `${formatHoursMinutes(displayHours)}${hoursSuffix}`;
          const parentPrefix = item.isSubitem && item.parentItemName
            ? `${item.parentItemName} > `
            : '';
          const subitemMarker = item.isSubitem ? '\u21B3 ' : '';
          rowData.push(`${subitemMarker}${parentPrefix}${item.name} (${hoursLabel})`);
          cellColors.push(item.color || '#579bfc');
          cellFontColors.push(null);
        } else {
          const leaveKey = `${user.id}_${ds}`;
          const leave = leaveMap.get(leaveKey);
          const holiday = holidayMap.get(leaveKey);
          // Show leave/holiday in the first empty slot (right after all items)
          const itemCount = dayItems.length;
          const isLeaveSlot = slot === itemCount;

          if (leave && isLeaveSlot) {
            const dayLabel = leave.dayType === 'full'
              ? ''
              : ` (${leave.dayType === 'am' ? 'AM' : 'PM'})`;
            rowData.push(`${leave.leaveType}${dayLabel} [${leave.status}]`);
            // Match scheduler colors: green=Approved, orange=Pending, red=Rejected, gray=Recalled
            const leaveColors: Record<string, { bg: string; font: string }> = {
              Approved: { bg: '#00c875', font: '#00c875' },
              Pending:  { bg: '#fdab3d', font: '#fdab3d' },
              Rejected: { bg: '#d83a52', font: '#d83a52' },
              Recalled: { bg: '#808080', font: '#808080' },
            };
            const lc = leaveColors[leave.status] ?? leaveColors.Pending;
            cellColors.push(lc.bg);
            cellFontColors.push(lc.font);
          } else if (holiday && isLeaveSlot) {
            rowData.push(holiday);
            cellColors.push('#ff9800');
            cellFontColors.push(null);
          } else if (isWeekend(day)) {
            rowData.push('');
            cellColors.push('#F0F0F0');
            cellFontColors.push(null);
          } else {
            rowData.push('');
            cellColors.push(null);
            cellFontColors.push(null);
          }
        }
      }

      const row = ws.addRow(rowData);
      row.height = 22;
      row.alignment = { vertical: 'middle', wrapText: true };
      row.font = { size: 9 };

      // Style resource name cell
      if (slot === 0) {
        const nameCell = row.getCell(1);
        nameCell.font = { bold: true, size: 10 };
        nameCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F6F8' } };
      }

      // Style data cells
      for (let colIdx = 0; colIdx < allDays.length; colIdx++) {
        const cell = row.getCell(colIdx + 2);
        const color = cellColors[colIdx + 1];
        const fontColor = cellFontColors[colIdx + 1];

        if (color) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(color) },
          };
          cell.font = {
            size: 7,
            color: { argb: 'FFFFFFFF' },
            bold: true,
          };
        }

        cell.border = { right: thinBorder, bottom: thinBorder };
      }
    }

    // ── Workload row ──
    const wlRowData: string[] = [''];
    for (const day of allDays) {
      const ds = format(day, 'yyyy-MM-dd');
      const wl = userWorkload.get(ds);
      if (!wl || isWeekend(day)) {
        wlRowData.push('');
      } else {
        // Show scheduled / available, e.g. "6h / 8h"
        wlRowData.push(`${formatHoursMinutes(wl.scheduledHours)} / ${formatHoursMinutes(wl.availableHours)}`);
      }
    }

    const wlRow = ws.addRow(wlRowData);
    wlRow.height = 18;
    wlRow.alignment = { horizontal: 'center', vertical: 'middle' };
    wlRow.font = { size: 8, bold: true };

    // Style workload cells with color
    for (let colIdx = 0; colIdx < allDays.length; colIdx++) {
      const cell = wlRow.getCell(colIdx + 2);
      const ds = format(allDays[colIdx], 'yyyy-MM-dd');
      const wl = userWorkload.get(ds);

      if (wl && !isWeekend(allDays[colIdx])) {
        const color = getWorkloadColor(
          wl.scheduledHours, wl.availableHours,
          wl.isLeave, wl.leaveDayType, wl.hasLeaveConflict,
          wl.isHoliday, wl.isPastDay,
        );

        if (color !== WORKLOAD_COLORS.empty) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(color) },
          };
          cell.font = { size: 8, bold: true, color: { argb: 'FFFFFFFF' } };
        }
      } else if (isWeekend(allDays[colIdx])) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
      }

      cell.border = { right: thinBorder, bottom: { style: 'medium', color: { argb: 'FFCCCCCC' } } };
    }

    // Style the workload label cell
    const wlLabelCell = wlRow.getCell(1);
    wlLabelCell.value = 'Workload';
    wlLabelCell.font = { size: 8, italic: true, color: { argb: 'FF888888' } };
    wlLabelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F6F8' } };

    // Merge resource name cells (item rows only, not workload row)
    if (maxOverlap > 1) {
      const wlRowNum = ws.rowCount;
      const firstItemRow = wlRowNum - maxOverlap;
      const lastItemRow = wlRowNum - 1;
      ws.mergeCells(firstItemRow, 1, lastItemRow, 1);
    }

    // Separator row
    const sepRow = ws.addRow([]);
    sepRow.height = 4;
  }

  // ── Legend section (below data) ──
  ws.addRow([]); // spacer
  ws.addRow([]); // spacer

  const legendTitle = ws.addRow(['How to read this report:']);
  legendTitle.font = { size: 9, bold: true, color: { argb: 'FF323338' } };

  const legendLines = [
    isRemainingMode
      ? 'Items — "Task Name (5h rem)" = task with 5h remaining estimate; "Task Name (2h/d)" = task with 2h per day estimate'
      : 'Items — "Task Name (8h)" = task with 8h original estimate; "Task Name (2h/d)" = task with 2h per day estimate',
    'Subitems — "\u21B3 Parent Item > Subitem Name" shows the parent item before the subitem name',
    `Workload row — "6h / 8h" = 6h scheduled / 8h available capacity for the day (mode: ${activeMode})`,
    'Workload colors: blue = underloaded, green = at capacity, yellow = near overload, red = overloaded',
    'Leaves — shown with status: [Approved] = green, [Pending] = orange, [Rejected] = red',
    'Holidays — public holidays from calendar, shown in orange',
    'Weekends — gray background columns',
  ];

  for (const line of legendLines) {
    const row = ws.addRow(['  ' + line]);
    row.font = { size: 8, color: { argb: 'FFB0B0B0' } };
  }

  ws.addRow([]); // spacer

  // Color legend swatches
  const swatchRow = ws.addRow(['Workload color legend:']);
  swatchRow.font = { size: 8, color: { argb: 'FFB0B0B0' } };

  const swatches: { label: string; color: string }[] = [
    { label: 'Underloaded', color: '#66ccff' },
    { label: 'At capacity', color: '#00c875' },
    { label: 'Near overload', color: '#fdab3d' },
    { label: 'Overloaded', color: '#e2445c' },
    { label: 'Leave', color: '#579bfc' },
    { label: 'Holiday', color: '#ff9800' },
    { label: 'Conflict', color: '#a25ddc' },
  ];

  const swatchDataRow = ws.addRow(['', ...swatches.map((s) => s.label)]);
  swatchDataRow.height = 20;
  swatchDataRow.alignment = { horizontal: 'center', vertical: 'middle' };
  for (let i = 0; i < swatches.length; i++) {
    const cell = swatchDataRow.getCell(i + 2);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(swatches[i].color) } };
    cell.font = { size: 8, bold: true, color: { argb: 'FFFFFFFF' } };
  }

  // Generate and download
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const safeName = format(startDate, 'yyyy-MM-dd') + '_' + format(endDate, 'yyyy-MM-dd');
  downloadBlob(blob, `schedule_${safeName}.xlsx`);
}
